import { Role } from "src/app/enums/role.enum";
import { canRoleAccess, getRoutePermission } from "src/app/permissions/rbac-policy";
import { NavItem } from "./nav-item/nav-item";

const adminMenu: NavItem[] = [
  { navCap: "Overview" },

  {
    displayName: "CRM Overview",
    iconName: "solar:widget-5-bold-duotone",
    route: "/app/dashboard",
  },

  {
    displayName: "Admin Analytics",
    iconName: "solar:chart-square-bold-duotone",
    route: "/app/analytics",
  },

  {
    displayName: "Funnel Dashboard",
    iconName: "solar:chart-2-bold-duotone",
    route: "/app/funnel-dashboard",
  },

  { navCap: "Lead Operations", divider: true },

  {
    displayName: "Lead Forms",
    iconName: "solar:clipboard-add-bold-duotone",
    route: "/admin/form-builder",
  },

  {
    displayName: "Pipeline Board",
    iconName: "solar:diagram-down-bold-duotone",
    route: "/app/pipeline-board",
  },

  {
    displayName: "Lead Management",
    iconName: "solar:inbox-bold-duotone",
    route: "/app/lead-generated",
  },

  {
    displayName: "Bookings Calendar",
    iconName: "solar:calendar-bold-duotone",
    route: "/app/calender",
  },

  { navCap: "Campaigns", divider: true },

  {
    displayName: "Campaigns",
    iconName: "solar:letter-unread-bold-duotone",
    route: "/app/campaign-email",
  },

  {
    displayName: "Email Setup",
    iconName: "solar:letter-bold-duotone",
    route: "/app/email-setup",
  },

  {
    displayName: "Groups",
    iconName: "solar:users-group-rounded-bold-duotone",
    route: "/app/audience-groups",
  },

  {
    displayName: "Email Reports",
    iconName: "solar:chart-bold-duotone",
    route: "/app/email-reports",
  },

  // {
  //   displayName: "Template Builder",
  //   iconName: "solar:document-add-bold-duotone",
  //   route: "/app/email-builder",
  // },

  {
    displayName: "Nurture Journeys",
    iconName: "solar:mailbox-bold-duotone",
    route: "/app/email-nurture",
  },


  { navCap: "Account", divider: true },

  {
    displayName: "Team Management",
    iconName: "solar:users-group-rounded-bold-duotone",
    route: "/admin/managers",
  },

  {
    displayName: "Profile Settings",
    iconName: "solar:user-circle-line-duotone",
    route: "/app/edit-profile",
  },

  {
    displayName: "Billing & Subscription",
    iconName: "solar:card-bold-duotone",
    route: "/app/billing/subscription",
  },
];

function isRouteAllowed(route: string, role: Role): boolean {
  const permission = getRoutePermission(route);
  return !permission || canRoleAccess(role, permission);
}

function filterMenu(menu: NavItem[], role: Role): NavItem[] {
  return menu
    .map((item) => {
      if (item.children?.length) {
        const children = filterMenu(item.children, role);
        if (!children.length) {
          return null;
        }

        return { ...item, children };
      }

      if (item.route && !isRouteAllowed(item.route, role)) {
        return null;
      }

      return item;
    })
    .filter((item): item is NavItem => !!item);
}

export const navItems: { [role: string]: NavItem[] } = {

  // ---------------- Super Admin ----------------
  superadmin: [
    { navCap: "Dashboard" },
    {
      displayName: "Dashboard",
      iconName: "solar:widget-5-bold-duotone",
      route: "/superadmin/admin-dashboard",
    },

    { divider: true, navCap: "Admin Management" },

    {
      displayName: "Admins",
      iconName: "solar:users-group-rounded-line-duotone",
      route: "",
      chip: true,
      children: [
        {
          displayName: "Admin List",
          iconName: "solar:users-group-rounded-line-duotone",
          route: "/superadmin/admins",
        },
        {
          displayName: "Add Admin",
          iconName: "solar:user-plus-line-duotone",
          route: "/superadmin/add-admin",
        },
      ],
    },

    { divider: true, navCap: "Doctor Management" },

    {
      displayName: "Doctors",
      iconName: "solar:user-id-bold-duotone",
      route: "",
      chip: true,
      children: [
        {
          displayName: "Doctor List",
          iconName: "solar:user-id-line-duotone",
          route: "/superadmin/doctors",
        },
        {
          displayName: "Add Doctor",
          iconName: "solar:user-plus-line-duotone",
          route: "/superadmin/doctors/add",
        },
      ],
    },

    { divider: true, navCap: "Tracking & Masters" },

    {
      displayName: "Audit Trail",
      iconName: "solar:chart-square-line-duotone",
      route: "/superadmin/audit-trail",
    },

    {
      displayName: "Master Data",
      iconName: "solar:clipboard-add-line-duotone",
      route: "/superadmin/master-data",
    },

    {
      displayName: "Insurance Details",
      iconName: "solar:shield-check-bold-duotone",
      route: "/superadmin/insurance-details",
    },

    {
      displayName: "Billing Requests",
      iconName: "solar:card-bold-duotone",
      route: "/superadmin/billing-requests",
    },

    {
      displayName: "Referred By Doctor Performance",
      iconName: "solar:chart-square-bold-duotone",
      route: "/superadmin/referral-performance",
    },

    { divider: true, navCap: "Settings" },

    {
      displayName: "Settings / Configuration",
      iconName: "solar:settings-bold-duotone",
      route: "/superadmin/settings",
    },

    {
      displayName: "Edit Profile",
      iconName: "solar:user-circle-line-duotone",
      route: "/superadmin/edit-profile",
    },

    { divider: true, navCap: "Reports" },

    {
      displayName: "Reports",
      iconName: "solar:chart-square-bold-duotone",
      route: "/superadmin/reports",
    },

    {
      displayName: "System Reports",
      iconName: "solar:chart-square-line-duotone",
      route: "/superadmin/system-reports",
    },

    { divider: true },
  ],

  // ---------------- ADMIN ----------------
  admin: adminMenu,

  // ---------------- MANAGER ----------------
  manager: filterMenu(adminMenu, Role.MANAGER),

  // ---------------- SALES ----------------
  sales: filterMenu(adminMenu, Role.SALES),

  // ---------------- CUSTOMER ----------------
  customer: [
    { navCap: "Dashboard" },

    {
      displayName: "Home",
      iconName: "solar:layout-dashboard-line-duotone",
      route: "/customer/dashboard",
    },

    { navCap: "Profile", divider: true },

    {
      displayName: "Edit Profile",
      iconName: "solar:user-circle-line-duotone",
      route: "/customer/edit-profile",
    },

    {
      displayName: "Address Book",
      iconName: "solar:location-line-duotone",
      route: "/customer/address-book",
    },

    { navCap: "Support", divider: true },

    {
      displayName: "Contact Support",
      iconName: "solar:headset-line-duotone",
      route: "/customer/contact-support",
    },
  ],
};
